// Standard library imports
import { useContext } from 'react';
// External library imports
import { Form, Formik } from 'formik';
import { UilPen } from '@iconscout/react-unicons';
import { UilTrash } from '@iconscout/react-unicons';
import { toast } from 'react-toastify';

// Internal module imports
import classes from '../../../../styles/AllDevices.module.css';
import Typography from '../../../../components/Typography/Typography';
import { DropdownComponent, Input } from '../../../../components/Inputs/Input';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import { useEffect, useState } from 'react';
import Table from '../../../../components/Table/Table';
import { dataInjestService } from '../../../../services/dataInjestService';
import { START_LOADER, STOP_LOADER } from '../../../../constants';
import { LoaderContext } from '../../../../context/LoaderContext';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import NewJSONEditor from '../../../../components/JSONEditor/NewJSONEditor';
import { CamelCaseToTextCase, Capitalize } from '../../../../utils/stringHelper';
import InjestClasses from './index.module.css';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import { mongooseSchemaValidation } from '../../../../validations/Root/mongooseSchemaValidation';

const getTypesLabel = (type) => {
    const label = Capitalize(CamelCaseToTextCase(type));
    return label;
};

const MongooseSchema = () => {
    const initialValues = {
        name: '',
        dataSchema: {},
    };

    const { dispatch: loaderDispatch } = useContext(LoaderContext);
    const startLoader = (payload) => loaderDispatch({ type: START_LOADER, payload });
    const stopLoader = (payload) => loaderDispatch({ type: STOP_LOADER, payload });
    const [addFormModal, setAddFormModal] = useState(false);
    const [formData, setFormData] = useState(initialValues);
    const [formTableData, setFormTableData] = useState([]);
    const [editSchemaModal, setEditSchemaModal] = useState({
        status: false,
        id: '',
    });
    const [deleteSchemaModal, setDeleteSchemaModal] = useState({
        status: false,
        id: '',
    });
    const [modelTypes, setModelTypes] = useState({});
    const [modelTypeOptions, setModelTypeOptions] = useState([]);
    const [updateDefaultValueEditor, setUpdateDefaultValueEditor] = useState(false);

    useEffect(() => {
        fetchMongooseSchema();
        fetchModelTypes();
    }, []);

    const fetchModelTypes = () => {
        dataInjestService.ReadAllModelTyes(
            () => startLoader('getDataInjestModels'),
            handleModelSuccess,
            handleError,
            () => stopLoader('getDataInjestModels')
        );
    };

    const handleModelSuccess = ({ data }) => {
        const types = data?.data?.types;
        if (types) {
            setModelTypes(data?.data?.types);
            const options = Object.keys(types).map((t) => ({
                label: getTypesLabel(t),
                value: t,
            }));
            console.log({ options });
            setModelTypeOptions(options);
        }
    };

    const fetchMongooseSchema = () => {
        dataInjestService.ReadAllSchema(
            () => startLoader('getMongooseSchema'),
            handleSuccess,
            handleError,
            () => stopLoader('getMongooseSchema')
        );
    };

    const handleSuccess = ({ data }) => {
        const processedData = data?.data.map((key, i) => ({
            's.no': i + 1,
            name: key.name,
            createdAt: key.createdAt ? momentTimeFormater(key.createdAt).format('YYYY-MM-DD HH:mm:ss') : '--',
            updatedAt: key.updatedAt ? momentTimeFormater(key.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '--',
            action: (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CustomTooltip content={'Edit'}>
                        <UilPen
                            size={'1.2vw'}
                            style={{ color: 'var(--color-primary)', marginRight: '0.5vw' }}
                            onClick={() => {
                                setFormData({
                                    name: key.name,
                                    dataSchema: key.dataSchema,
                                });
                                setEditSchemaModal({
                                    status: true,
                                    id: key._id,
                                });
                            }}
                        />
                    </CustomTooltip>
                    <CustomTooltip content={'Delete'}>
                        <UilTrash
                            size={'1.2vw'}
                            style={{ color: 'var(--color-primary)' }}
                            onClick={() =>
                                setDeleteSchemaModal({
                                    status: true,
                                    id: key._id,
                                })
                            }
                        />
                    </CustomTooltip>
                </div>
            ),
        }));
        setFormTableData(processedData);
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    const handleSubmit = (values) => {
        const payload = { ...values };
        if (editSchemaModal.status) {
            dataInjestService.updateSchema(
                editSchemaModal.id,
                payload,
                () => startLoader('updateSchema'),
                handleEditSuccess,
                handleError,
                () => stopLoader('updateSchema')
            );
            return;
        }
        let params = {};
        dataInjestService.createSchema(
            payload,
            params,
            () => startLoader('deleteSchema'),
            handleCreateSuccess,
            handleError,
            () => stopLoader('deleteSchema')
        );
    };

    const handleCreateSuccess = (res) => {
        fetchMongooseSchema();
        toast.success('Schema created sucessfully');
        setAddFormModal(false);
        setFormData(initialValues);
    };

    const handleEditSuccess = (res) => {
        fetchMongooseSchema();
        toast.success('Schema updated sucessfully');
        setEditSchemaModal({ status: false, id: '' });
        setFormData(initialValues);
    };

    const handleDelete = () => {
        if (deleteSchemaModal.id) {
            dataInjestService.deleteSchema(
                deleteSchemaModal.id,
                startLoader,
                () => {
                    fetchMongooseSchema();
                    toast.success('Schema deleted sucessfully');
                },
                handleError,
                stopLoader
            );
        }
    };

    const handleModelTypesChange = (e, setFieldValue) => {
        setFieldValue('dataSchema', modelTypes[e.value]);
    };

    return (
        <div className={classes.AllDevices}>
            <ModalComponent isOpen={addFormModal || editSchemaModal.status} setOpen={(status) => setAddFormModal(status)} style={{ overflow: 'initial' }}>
                <div className={InjestClasses.ModalWrapper} style={{ 'minWidth': '40vw' }}>
                    <Typography content={addFormModal ? 'Add Mongoose Schema' : 'Edit Mongoose Schema'} />
                    <div className={classes.FormContent}>
                        <Formik initialValues={formData} enableReinitialize validationSchema={mongooseSchemaValidation} onSubmit={(val, { resetForm }) => handleSubmit(val, resetForm)}>
                            {({ errors, touched, values, isValidating, ...props }) => {
                                return (
                                    <Form>
                                        <div>

                                            <div className={classes.InputContainer}>
                                                <div className={classes.FieldControl2}>
                                                    <label for="name">
                                                        Name <span className="required">*</span>
                                                    </label>
                                                    <Input
                                                        name="name"
                                                        id="name"
                                                        setFieldValue={props.setFieldValue}
                                                        setFieldTouched={props.setFieldTouched}
                                                        getFieldMeta={props.getFieldMeta}
                                                        style={{ marginTop: '0' }}
                                                    />
                                                </div>
                                                <div className={classes.FieldControl2}>
                                                    <label for="predefinedSchema">Predefined Schema</label>
                                                    <DropdownComponent
                                                        name="predefinedSchema"
                                                        label="Predefined Schema"
                                                        options={modelTypeOptions}
                                                        onChange={(e) => {
                                                            setUpdateDefaultValueEditor(true);
                                                            handleModelTypesChange(e, props.setFieldValue);
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className={classes.FieldControl2}>
                                                <label for="dataSchema">
                                                    Data Schema <span className="required">*</span>
                                                </label>
                                                <NewJSONEditor
                                                    name="dataSchema"
                                                    defaultValue={values.dataSchema}
                                                    height="18rem"
                                                    manualDefaultValueUpdate={updateDefaultValueEditor}
                                                    setManualDefaultValueUpdate={setUpdateDefaultValueEditor}
                                                />
                                            </div>
                                            <div className={classes.ButtonContainer}>
                                                <div>
                                                    <button
                                                        className="btn-secondary"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            addFormModal
                                                                ? setAddFormModal(false)
                                                                : setEditSchemaModal({
                                                                    status: false,
                                                                    id: '',
                                                                });
                                                            setFormData(initialValues);
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                                <div>
                                                    <button className="btn-primary" type="submit">
                                                        Submit
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            </ModalComponent>
            <DeleteModal
                deletefunction={handleDelete}
                opendeleteModal={deleteSchemaModal.status}
                setOpenDeleteModal={(status) => setDeleteSchemaModal({ ...deleteSchemaModal, status })}
            />
            <div className={classes.Header}>
                <div>
                    <Typography content="Mongoose Schema" />
                    <div className={classes.TableCount}>
                        Total Count :<span>{formTableData.length}</span>
                    </div>
                    <div>
                        <button
                            type="submit"
                            className="btn-primary"
                            style={{ marginTop: '0' }}
                            onClick={() => {
                                setAddFormModal(true);
                                setEditSchemaModal({ status: false, id: '' });
                            }}
                        >
                            Add
                        </button>
                    </div>
                </div>
            </div>
            <div>
                <Table
                    head={['S.No', 'Name', 'Created At (CET)', 'Updated At (CET)', 'Action']}
                    keys={['s.no', 'name', 'createdAt', 'updatedAt', 'action']}
                    data={formTableData}
                />
            </div>
        </div>
    );
};

export default MongooseSchema;
